import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Package, History, AlertTriangle, Edit2, Check, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pos`;

const StockManagement = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [products, setProducts] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchSummary();
    if (activeTab === 'raw_materials') {
      fetchRawMaterials();
    } else {
      fetchProducts();
    }
  }, [activeTab, search]);

  const fetchSummary = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/stock/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch stock summary');
    }
  };

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const params = {};
      if (activeTab !== 'all') params.status = activeTab;
      if (search) params.search = search;
      
      const response = await axios.get(`${API}/stock/list`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      // Filter out raw materials from regular stock view
      setProducts(response.data.filter(p => !p.exception_for_sale));
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRawMaterials = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/raw-materials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      let materials = response.data;
      
      // Filter by search if needed
      if (search) {
        const searchLower = search.toLowerCase();
        materials = materials.filter(m => 
          m.name.toLowerCase().includes(searchLower) ||
          m.sku?.toLowerCase().includes(searchLower)
        );
      }
      
      setRawMaterials(materials);
    } catch (err) {
      console.error('Error fetching raw materials:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setEditForm({
      current_stock: product.current_stock,
      mrp: product.mrp,
      selling_price: product.selling_price,
      stock_alert_quantity: product.stock_alert_quantity
    });
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      await axios.put(`${API}/stock/${editingProduct.id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Stock updated successfully');
      setEditingProduct(null);
      fetchSummary();
      if (activeTab === 'raw_materials') {
        fetchRawMaterials();
      } else {
        fetchProducts();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update stock');
    }
  };

  const formatCurrency = (amount) => `₹${(amount || 0).toFixed(2)}`;

  const getStockStatus = (product) => {
    if (product.current_stock <= 0) return { label: 'Out', color: 'red' };
    if (product.current_stock <= product.stock_alert_quantity) return { label: 'Low', color: 'amber' };
    return { label: 'OK', color: 'green' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  const displayProducts = activeTab === 'raw_materials' ? rawMaterials : products;

  return (
    <div data-testid="stock-management-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Stock Management</h1>
          <p className="text-slate-500">Manage inventory and stock levels</p>
        </div>
        {activeTab === 'raw_materials' && (
          <button
            onClick={() => navigate('/gposp/raw-material-usage')}
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-xl font-medium hover:bg-amber-700"
            data-testid="record-usage-btn"
          >
            <Package size={18} />
            Record Usage
          </button>
        )}
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
          {error}
          <button onClick={() => setError('')} className="ml-2 text-red-500">×</button>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-700">
          {success}
          <button onClick={() => setSuccess('')} className="ml-2 text-green-500">×</button>
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-sm text-slate-500">Total Products</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">{summary.total_products}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-sm text-slate-500">Available</p>
            <p className="text-2xl font-bold text-emerald-600 mt-1">{summary.available_count}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-sm text-slate-500">Low Stock</p>
            <p className="text-2xl font-bold text-slate-700 mt-1">{summary.low_stock_count}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-sm text-slate-500">Out of Stock</p>
            <p className="text-2xl font-bold text-red-600 mt-1">{summary.out_of_stock_count}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-sm text-slate-500">Stock Value</p>
            <p className="text-2xl font-bold text-violet-600 mt-1">{formatCurrency(summary.total_stock_value)}</p>
          </div>
        </div>
      )}

      {/* Tabs and Search */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'all', label: 'All Products' },
              { key: 'available', label: 'Available' },
              { key: 'low', label: 'Low Stock' },
              { key: 'out', label: 'Out of Stock' },
              { key: 'raw_materials', label: 'Raw Materials', highlight: true }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => { setActiveTab(tab.key); setLoading(true); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.key
                    ? tab.highlight ? 'bg-amber-100 text-amber-700' : 'bg-violet-100 text-violet-700'
                    : tab.highlight ? 'text-amber-600 hover:bg-amber-50 border border-amber-200' : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid={`tab-${tab.key}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products..."
            className="w-full md:w-64 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
            data-testid="search-stock"
          />
        </div>
      </div>

      {/* Raw Materials Info Banner */}
      {activeTab === 'raw_materials' && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <Package className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="font-medium text-amber-800">Raw Materials View</p>
            <p className="text-sm text-amber-700">
              These are products marked as "Raw Material (Not for Sale)". They are tracked in inventory but won't appear in billing.
              Use "Record Usage" to deduct stock when materials are consumed.
            </p>
          </div>
        </div>
      )}

      {/* Products Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Product</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">SKU</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-slate-600">Stock</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-slate-600">Alert Qty</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-slate-600">Status</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-slate-600">Cost</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-slate-600">MRP</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {displayProducts.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-slate-500">
                    {activeTab === 'raw_materials' ? (
                      <div>
                        <Package className="mx-auto mb-3 text-slate-300" size={48} />
                        <p>No raw materials found</p>
                        <p className="text-sm mt-1">Mark products as "Raw Material" in the Products page</p>
                      </div>
                    ) : (
                      'No products found'
                    )}
                  </td>
                </tr>
              ) : (
                displayProducts.map((product) => {
                  const status = getStockStatus(product);
                  const isEditing = editingProduct?.id === product.id;
                  
                  return (
                    <tr key={product.id} className="hover:bg-slate-50" data-testid={`stock-row-${product.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                            <Package size={18} className="text-slate-400" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{product.name}</p>
                            {product.category_name && (
                              <p className="text-xs text-slate-500">{product.category_name}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-slate-600">{product.sku || '-'}</td>
                      <td className="px-4 py-3 text-right">
                        {isEditing ? (
                          <input
                            type="number"
                            value={editForm.current_stock}
                            onChange={(e) => setEditForm({ ...editForm, current_stock: parseFloat(e.target.value) })}
                            className="w-20 px-2 py-1 border border-violet-300 rounded text-right"
                          />
                        ) : (
                          <span className="font-semibold">{product.current_stock}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isEditing ? (
                          <input
                            type="number"
                            value={editForm.stock_alert_quantity}
                            onChange={(e) => setEditForm({ ...editForm, stock_alert_quantity: parseFloat(e.target.value) })}
                            className="w-20 px-2 py-1 border border-violet-300 rounded text-right"
                          />
                        ) : (
                          <span className="text-slate-500">{product.stock_alert_quantity}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium bg-${status.color}-100 text-${status.color}-700`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-slate-600">{formatCurrency(product.base_price)}</td>
                      <td className="px-4 py-3 text-right">
                        {isEditing ? (
                          <input
                            type="number"
                            value={editForm.mrp}
                            onChange={(e) => setEditForm({ ...editForm, mrp: parseFloat(e.target.value) })}
                            className="w-24 px-2 py-1 border border-violet-300 rounded text-right"
                          />
                        ) : (
                          <span className="font-medium">{formatCurrency(product.mrp)}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          {isEditing ? (
                            <>
                              <button
                                onClick={handleSaveEdit}
                                className="p-1.5 hover:bg-green-50 text-green-600 rounded-lg"
                                title="Save"
                              >
                                <Check size={16} />
                              </button>
                              <button
                                onClick={() => setEditingProduct(null)}
                                className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg"
                                title="Cancel"
                              >
                                <X size={16} />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleEdit(product)}
                                className="p-1.5 hover:bg-violet-50 text-violet-600 rounded-lg"
                                title="Edit Stock"
                              >
                                <Edit2 size={16} />
                              </button>
                              {activeTab === 'raw_materials' && (
                                <button
                                  onClick={() => navigate(`/gposp/raw-material-history?product=${product.id}`)}
                                  className="p-1.5 hover:bg-amber-50 text-amber-600 rounded-lg"
                                  title="Usage History"
                                >
                                  <History size={16} />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StockManagement;
